import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { Textarea } from "@/components/ui/baseUi/textarea"
import { word } from "@/modules/core/utils/i18n"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import type { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { UserMentionList } from "@/modules/social/components/userMentionList"
import type { CommentMentionDto } from "@/modules/social/gateway/socialDto"
import { useCreateComment } from "@/modules/social/hooks/useSocialCommentActions"
import { useUser } from "@/modules/users/hooks/useUser"
import { SendIcon, XIcon } from "lucide-react"
import { useEffect, useState } from "react"

export type SocialCommentInputProps = {
  socialPostId: string | null
  sessionId?: string
  exerciseId?: string
  userId?: string
  parentCommentId: string | null
  replyTo?: string
  placeholder?: string
  onSubmit?: () => void
  onCancelReply?: () => void
  ref: React.RefObject<HTMLTextAreaElement | null>
}

export function SocialCommentInput({
  socialPostId,
  sessionId,
  exerciseId,
  userId,
  parentCommentId,
  replyTo,
  onCancelReply,
  placeholder = word("social.comment.input.placeholder"),
  onSubmit,
  ref,
}: SocialCommentInputProps) {
  const [text, setText] = useState("")
  const [mentions, setMentions] = useState<CommentMentionDto[]>([])
  const [mentionKeyword, setMentionKeyword] = useState<string | null>(null)
  const [mentionStartIndex, setMentionStartIndex] = useState(0)

  useEffect(() => {
    if (replyTo) {
      setText(`@${replyTo} `)
    }
    if (!replyTo) {
      setText("")
    }
  }, [replyTo])

  const { mutateAsync: createComment, isPending } = useCreateComment({
    socialPostId,
    sessionId: sessionId ?? null,
    exerciseId: exerciseId ?? null,
    userId: userId ?? null,
  })
  const user = useUser()

  const detectMention = (value: string, cursorPos: number) => {
    const textBeforeCursor = value.slice(0, cursorPos)
    const match = textBeforeCursor.match(/@(\w*)$/)
    if (match) {
      setMentionKeyword(match[1] ?? "")
      setMentionStartIndex(cursorPos - match[0].length)
    } else {
      setMentionKeyword(null)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setText(value)
    detectMention(value, e.target.selectionStart ?? value.length)
  }

  const handleSelectMention = (selectedUser: UserEntity) => {
    const before = text.slice(0, mentionStartIndex)
    const after = text.slice(mentionStartIndex + (mentionKeyword?.length ?? 0) + 1) // +1 for @
    const newText = `${before}@${selectedUser.username} ${after}`
    setText(newText)

    setMentions((prev) => [
      ...prev,
      {
        username: selectedUser.username,
        userId: selectedUser.id,
        startAt: mentionStartIndex,
        length: selectedUser.username.length,
      },
    ])
    setMentionKeyword(null)

    // Restore focus to textarea
    setTimeout(() => ref.current?.focus(), 0)
  }

  const submitComment = async () => {
    if (!text.trim() || isPending) return
    await createComment(
      { text: text.trim(), parentCommentId: parentCommentId ?? null, medias: [], mentions },
      {
        onSuccess: () => {
          setText("")
          setMentions([])
          setMentionKeyword(null)
          onSubmit?.()
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape" && mentionKeyword !== null) {
      e.preventDefault()
      setMentionKeyword(null)
      return
    }
    if (e.key === "Enter" && !e.shiftKey && mentionKeyword === null) {
      e.preventDefault()
      void submitComment()
    }
  }

  return (
    <div className="relative w-full">
      {/* Mention suggestion list */}
      {mentionKeyword !== null && mentionKeyword.length > 0 && (
        <div className="absolute right-0 bottom-full left-0 z-50 m-1 rounded-xl border border-interactive-border-neutral-faded bg-surface-background-default shadow-lg">
          <UserMentionList socialPostId={socialPostId} keyword={mentionKeyword} onSelect={handleSelectMention} />
        </div>
      )}

      <div
        className="flex w-full cursor-text flex-col gap-2 border-t border-interactive-background-neutral-faded px-4 py-3"
        onClick={() => ref.current?.focus()}
      >
        {replyTo && (
          <div className="flex items-center">
            <Text fontStyle="LS" className="text-textOnSurface-muted">
              {word("social.comment.input.reply")}{" "}
              <Text fontStyle="LS" className="text-textOnSurface-default">
                {replyTo}
              </Text>
            </Text>
            <button
              className="shrink-0 cursor-pointer rounded-full px-1 text-textOnSurface-default"
              onClick={onCancelReply}
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
        <div className="flex w-full items-center gap-2">
          <ProfileAvatar profile={{ avatar: user.avatar ?? null, username: user.username }} className="size-10" />
          <Textarea
            ref={ref}
            value={text}
            onChange={handleChange}
            placeholder={placeholder}
            className="flex min-h-0 resize-none rounded-none border-none bg-transparent p-0 text-base leading-normal break-all text-textOnSurface-default shadow-none placeholder:text-textOnSurface-disabled focus-visible:ring-0"
            maxLength={240}
            autoFocus
            rows={1}
            wrap="hard"
            onKeyDown={handleKeyDown}
            onBlur={() => {
              if (text.trim().length === 0) setText("")
            }}
          />
          <Button
            type="submit"
            onClick={() => {
              void submitComment()
            }}
            disabled={!text.trim() || isPending}
            variant="ghost"
            size="iconMd"
            className="mb-1 shrink-0 rounded-full text-textOnSurface-default"
          >
            <SendIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
